import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectorItem } from '@app/component/selector/selector.component';
import { Market } from '@app/model/payment-request/payment-request.dto';
import { RouteComponent } from '@app/route/route.component';
import { LoanOptionVM } from '@app/route/shared/LoanOptionVM';
import { HeaderService } from '@app/service/header/header.service';
import { FiUserService } from '@app/service/fi-user-service/fi-user.service';
import { StateService } from '@app/service/state/state.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import Translations from './fi-installments-selection.component.json';

@Component({
  selector: 'fi-installments-selection',
  templateUrl: './fi-installments-selection.component.html',
  styleUrls: [
    '../../page-shared.css',
    './fi-installments-selection.component.scss',
  ],
})
export class FiInstallmentsSelectionComponent
  extends RouteComponent
  implements OnInit
{
  availableLoanOptions: LoanOptionVM[] = null;
  presentedLoanOptions: LoanOptionVM[] = null;
  selectedLoanOption: LoanOptionVM = null;
  expanded: boolean = false;
  equalOrLessThanThreeLoanOptions: boolean = false;
  navigationInProgress: boolean = false;
  currentLoanTermsStr: string = '';
  Market = Market;
  selectorItems: SelectorItem[] = null;
  modalActive: boolean = false;
  loading: boolean = true;

  translations: { [key: string]: string };

  preferredInstallmentPlanDurations: number[] = null;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private fiUserService: FiUserService,
    private valuePipe: ValuePipe
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    this.translations =
      Translations[
        this.fiUserService.getCurrentStateLocaleBaseCode(this.state.language)
      ];

    await this.setup();
  }

  async setup(): Promise<void> {
    this.availableLoanOptions = this.fiUserService.transformDtosTOVMs(
      this.state.loanOptions.sort((a, b) => a.monthlyCost - b.monthlyCost),
      this.toggleModal.bind(this),
      this.state.language
    );

    this.equalOrLessThanThreeLoanOptions =
      this.availableLoanOptions.length <= 3;

    this.getPresentedLoanOptions();
    this.selectedLoanOption = this.presentedLoanOptions[0];
    this.selectInstallment(this.selectedLoanOption);
    this.loading = false;
  }

  async handleCtaClick(): Promise<void> {
    try {
      await this.fiUserService.selectInstallmentPlan(
        this.selectedLoanOption.termId
      );
      this.navigate();
    } catch (error) {
      console.log(error);
    }
  }

  selectInstallment(loanOption: LoanOptionVM): void {
    this.selectedLoanOption = loanOption;
    this.currentLoanTermsStr = this.fiUserService.buildLoanTermsString(
      this.selectedLoanOption,
      this.state.totalCheckoutValue,
      this.state.language
    );
    this.selectorItems = [
      {
        tab: this.translations.modalTabSummary,
        title: this.translations.modalTitleSummary,
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `${
          this.translations.modalTotalAmountIncVat
        } ${this.valuePipe.transform(this.state.totalCheckoutValue)}`,
      },
      {
        tab: this.translations.modalTabTermsOfPayment,
        title: this.translations.modalTitleTermsOfPayment,
        descriptions: [{ text: this.currentLoanTermsStr }],
        ...(!!loanOption.loanTermsURL && {
          links: [
            {
              text: this.translations.modalLinkSecci,
              onClick: () => this.openURLinNewWindow(loanOption.secciURL),
            },
            {
              text: this.translations.modalLinkCreditAgreement,
              onClick: () => this.openURLinNewWindow(loanOption.loanTermsURL),
            },
          ],
        }),
      },
    ];
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }

  getPresentedLoanOptions(): void {
    if (this.equalOrLessThanThreeLoanOptions) {
      this.presentedLoanOptions = this.availableLoanOptions;
      return;
    }

    const middleMostIndex: number = Math.floor(
      this.availableLoanOptions.length / 2
    );

    const longest: LoanOptionVM = this.availableLoanOptions[0];
    const middlemost: LoanOptionVM = this.availableLoanOptions[middleMostIndex];
    const shortest: LoanOptionVM =
      this.availableLoanOptions[this.availableLoanOptions.length - 1];

    this.presentedLoanOptions = [longest, middlemost, shortest];

    if (this.preferredInstallmentPlanDurations?.length > 0) {
      this.presentedLoanOptions = this.availableLoanOptions.filter(
        (loanOption: LoanOptionVM) =>
          this.preferredInstallmentPlanDurations.includes(
            loanOption.numberOfMonths
          )
      );
    }

    // if we have selected installment, replace closest
    if (this.selectedLoanOption) {
      const closestLoanOption: LoanOptionVM = this.getClosestLoanOption(
        this.presentedLoanOptions,
        this.selectedLoanOption.monthlyCost
      );

      // replace closest installment with selected loan option
      this.presentedLoanOptions = this.presentedLoanOptions.map(
        (loanOption: LoanOptionVM) =>
          loanOption.monthlyCost === closestLoanOption.monthlyCost
            ? this.selectedLoanOption
            : loanOption
      );
    }
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  viewAllLoanOptions(): void {
    this.expanded = true;
    this.presentedLoanOptions = this.availableLoanOptions;
  }

  viewReducedLoanOptions(): void {
    this.expanded = false;
    this.getPresentedLoanOptions();
  }

  getClosestLoanOption(
    loanOption: LoanOptionVM[],
    selectedLoanOptionMonthlyCost: number
  ): LoanOptionVM {
    const monthlyCostDiff = (monthlyCost: number) =>
      Math.abs(monthlyCost - selectedLoanOptionMonthlyCost);
    return loanOption.reduce((prev: LoanOptionVM, curr: LoanOptionVM) =>
      monthlyCostDiff(curr.monthlyCost) < monthlyCostDiff(prev.monthlyCost)
        ? curr
        : prev
    );
  }
}
